/**
 * Preset swatches + custom hex / native color input for workspace accent.
 * Any valid #rrggbb is fine - UI tints derive from --ws-color via color-mix.
 */

import { useEffect, useState } from 'react'
import { Pipette } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import {
  DEFAULT_WORKSPACE_COLOR,
  normalizeWorkspaceHex,
  WORKSPACE_COLOR_PRESETS,
} from '@/lib/workspace-color'

type WorkspaceColorPickerProps = {
  value: string | null | undefined
  onChange: (hex: string) => void
  disabled?: boolean
  id?: string
}

export function WorkspaceColorPicker({
  value,
  onChange,
  disabled = false,
  id = 'workspace-color',
}: WorkspaceColorPickerProps) {
  const selected =
    normalizeWorkspaceHex(value ?? '') ?? DEFAULT_WORKSPACE_COLOR
  const isPreset = WORKSPACE_COLOR_PRESETS.some((p) => p.value === selected)
  const [hexDraft, setHexDraft] = useState(selected)
  const [hexError, setHexError] = useState<string | null>(null)

  useEffect(() => {
    setHexDraft(selected)
    setHexError(null)
  }, [selected])

  const commitHex = (raw: string) => {
    const normalized = normalizeWorkspaceHex(raw)
    if (!normalized) {
      setHexError('Use a 3- or 6-digit hex color (e.g. #c45a11)')
      return
    }
    setHexError(null)
    setHexDraft(normalized)
    if (normalized !== selected) onChange(normalized)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {WORKSPACE_COLOR_PRESETS.map(({ value: preset, label }) => (
          <button
            key={preset}
            type="button"
            title={label}
            disabled={disabled}
            aria-label={label}
            aria-pressed={selected === preset}
            className={cn(
              'h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 disabled:pointer-events-none disabled:opacity-50',
              selected === preset
                ? 'border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30'
                : 'border-transparent',
            )}
            style={{ backgroundColor: preset }}
            onClick={() => onChange(preset)}
          />
        ))}
        <label
          className={cn(
            'relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-dashed transition-transform hover:scale-110',
            !isPreset
              ? 'border-foreground ring-2 ring-offset-2 ring-offset-background ring-foreground/30'
              : 'border-muted-foreground/40',
            disabled && 'pointer-events-none opacity-50',
          )}
          title="Custom color"
          style={!isPreset ? { backgroundColor: selected } : undefined}
        >
          {isPreset ? (
            <Pipette className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <Pipette className="h-3.5 w-3.5 text-white drop-shadow-sm" />
          )}
          <input
            type="color"
            className="absolute inset-0 cursor-pointer opacity-0"
            value={selected}
            disabled={disabled}
            aria-label="Pick custom workspace color"
            onChange={(e) => {
              const next = normalizeWorkspaceHex(e.target.value)
              if (next) onChange(next)
            }}
          />
        </label>
      </div>

      <div className="flex max-w-xs items-end gap-2">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Label htmlFor={`${id}-hex`} className="text-xs text-muted-foreground">
            Custom hex
          </Label>
          <Input
            id={`${id}-hex`}
            value={hexDraft}
            disabled={disabled}
            placeholder="#c45a11"
            spellCheck={false}
            className="font-mono text-sm"
            onChange={(e) => {
              setHexDraft(e.target.value)
              setHexError(null)
            }}
            onBlur={() => commitHex(hexDraft)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitHex(hexDraft)
              }
            }}
          />
        </div>
        <div
          className="mb-0.5 h-9 w-9 shrink-0 rounded-md border border-border"
          style={{ backgroundColor: selected }}
          title={selected}
          aria-hidden
        />
      </div>
      {hexError ? <p className="text-xs text-destructive">{hexError}</p> : null}
    </div>
  )
}
